/**
 * RBAC permission catalog. Permission strings are stored on Role.permissions
 * and checked by the API's PermissionsGuard. Format: `<module>:<action>`.
 */

export const PERMISSION_MODULES = [
  'dashboard',
  'live',
  'history',
  'vehicles',
  'groups',
  'drivers',
  'devices',
  'customers',
  'places',
  'trips',
  'expenses',
  'fuel',
  'invoices',
  'maintenance',
  'workorders',
  'documents',
  'alerts',
  'alertrules',
  'reports',
  'users',
  'roles',
  'settings',
  'audit',
] as const;
export type PermissionModule = (typeof PERMISSION_MODULES)[number];

const BASE_ACTIONS = ['read', 'create', 'update', 'delete'] as const;

const EXTRA_ACTIONS: Partial<Record<PermissionModule, readonly string[]>> = {
  expenses: ['approve'],
  trips: ['dispatch'],
  reports: ['export'],
  alerts: ['assign'],
  vehicles: ['assign'],
  devices: ['link'],
};

const READ_ONLY_MODULES: readonly PermissionModule[] = ['dashboard', 'live', 'history', 'audit'];

function buildCatalog(): string[] {
  const out: string[] = [];
  for (const m of PERMISSION_MODULES) {
    if (READ_ONLY_MODULES.includes(m)) {
      out.push(`${m}:read`);
    } else {
      for (const a of BASE_ACTIONS) out.push(`${m}:${a}`);
    }
    for (const a of EXTRA_ACTIONS[m] ?? []) out.push(`${m}:${a}`);
  }
  return out;
}

/** Every valid permission string. */
export const PERMISSIONS: readonly string[] = Object.freeze(buildCatalog());

export type Permission = (typeof PERMISSIONS)[number];

export const isPermission = (s: string): s is Permission => PERMISSIONS.includes(s);

/** System role codes seeded for every tenant on provisioning. */
export const SYSTEM_ROLE_CODES = [
  'SYSTEM_ADMIN',
  'FLEET_MANAGER',
  'DISPATCHER',
  'ACCOUNTANT',
  'MAINTENANCE',
  'VIEWER',
  'DRIVER',
] as const;
export type SystemRoleCode = (typeof SYSTEM_ROLE_CODES)[number];

const all = (m: PermissionModule) => PERMISSIONS.filter((p) => p.startsWith(`${m}:`));
const read = (...ms: PermissionModule[]) => ms.map((m) => `${m}:read`);

export const ROLE_PRESETS: Record<
  SystemRoleCode,
  { nameAr: string; nameEn: string; permissions: string[] }
> = {
  SYSTEM_ADMIN: {
    nameAr: 'مدير النظام',
    nameEn: 'System Admin',
    permissions: [...PERMISSIONS],
  },
  FLEET_MANAGER: {
    nameAr: 'مدير الأسطول',
    nameEn: 'Fleet Manager',
    permissions: [
      ...read('dashboard', 'live', 'history', 'audit'),
      ...all('vehicles'),
      ...all('groups'),
      ...all('drivers'),
      ...all('devices'),
      ...all('customers'),
      ...all('places'),
      ...all('trips'),
      ...all('expenses'),
      ...all('fuel'),
      ...all('maintenance'),
      ...all('workorders'),
      ...all('documents'),
      ...all('alerts'),
      ...all('alertrules'),
      ...all('reports'),
      ...read('invoices', 'users', 'roles', 'settings'),
    ],
  },
  DISPATCHER: {
    nameAr: 'مشرف تشغيل',
    nameEn: 'Dispatcher',
    permissions: [
      ...read('dashboard', 'live', 'history', 'vehicles', 'drivers', 'customers', 'places', 'reports'),
      'places:create',
      'places:update',
      ...all('trips'),
      'alerts:read',
      'alerts:update',
      'alerts:assign',
      'expenses:read',
    ],
  },
  ACCOUNTANT: {
    nameAr: 'محاسب',
    nameEn: 'Accountant',
    permissions: [
      ...read('dashboard', 'vehicles', 'drivers', 'customers', 'trips'),
      ...all('expenses'),
      ...all('fuel'),
      ...all('invoices'),
      'reports:read',
      'reports:export',
      'workorders:read',
    ],
  },
  MAINTENANCE: {
    nameAr: 'مسؤول صيانة',
    nameEn: 'Maintenance',
    permissions: [
      ...read('dashboard', 'vehicles', 'drivers', 'live'),
      ...all('maintenance'),
      ...all('workorders'),
      ...all('documents'),
      'alerts:read',
      'reports:read',
    ],
  },
  VIEWER: {
    nameAr: 'مشاهد',
    nameEn: 'Viewer',
    permissions: read(
      'dashboard',
      'live',
      'history',
      'vehicles',
      'drivers',
      'customers',
      'places',
      'trips',
      'expenses',
      'maintenance',
      'alerts',
      'reports',
    ),
  },
  DRIVER: {
    nameAr: 'سائق',
    nameEn: 'Driver',
    // Drivers use the /driver/** surface guarded by @DriverOnly(); no admin permissions.
    permissions: [],
  },
};
